/**
 * useActiveIntakeSeasons — web client hook for the member-onboarding-flow
 * intake-window visibility gate.
 *
 * This is the thin I/O adapter over the pure `activeIntakeSeasons(seasons, now)`
 * core (`infrastructure/lambda/pageant-year/seasonResolvers.ts`). It answers the
 * portal's "is there anywhere to apply right now?" question by loading every
 * Season from the Season SSOT (`dev-PageantYear`) via the SAME cached
 * `listSeasons` data path that backs `useIntakeSeason` / `useSeasons`, then
 * filtering to the intake-open set for the current instant.
 *
 * Design contract (see `.kiro/specs/member-onboarding-flow/design.md`,
 * "Components and Interfaces → 5. Portal window-gate adapters"):
 *   - Loads Seasons from `dev-PageantYear` (reusing the `useActiveSeason` data
 *     path — here the shared `useSeasons()` cached `listSeasons` query).
 *   - Returns `activeIntakeSeasons(seasons, new Date())`. The clock is injected
 *     at THIS I/O boundary; the pure core never calls `new Date()`.
 *   - A failed or empty Season read yields an EMPTY set (fail-closed): callers
 *     then show no application/profile surface (Req 3.2, 3.4). `useSeasons()`
 *     already normalizes both a rejected `listSeasons` query and a null/empty
 *     result to `[]`, so the fail-closed behavior falls out of reusing it.
 *
 * Recompute-on-resolution, not live re-render (Req 3.5, 6.5): `now` is read at
 * evaluation time. A season opening mid-session becomes reachable on the
 * member's next resolution (next mount / `refetch()`); an already-rendered view
 * need not update before then.
 *
 * Requirements: 3.1, 3.2, 3.3, 6.5 (member-onboarding-flow)
 */
import { useMemo } from 'react';

import { activeIntakeSeasons } from '../../../infrastructure/lambda/pageant-year/seasonResolvers';

import {
  useSeasons,
  type Season,
  type SeasonResolutionStatus,
} from '@/hooks/useActiveSeason';

/** Shape returned by `useActiveIntakeSeasons`. */
export interface UseActiveIntakeSeasonsResult {
  /**
   * The `Active_Intake_Season`s at evaluation time — exactly those Seasons whose
   * intake window is open now (`isIntakeOpen`), ascending by `competitionYear`.
   * Empty when none is open, when the read failed, or while loading
   * (fail-closed — Req 3.2, 3.4).
   */
  seasons: Season[];
  /**
   * `loading` while the underlying Season read is in flight; `resolved` when at
   * least one Active_Intake_Season exists; `unresolved` when the set is empty
   * (no open window, empty list, or a failed read).
   */
  status: SeasonResolutionStatus;
  /** Bust the season-list cache and re-run the query. */
  refetch: () => void;
}

/**
 * Resolve the set of `Active_Intake_Season`s at `now`.
 *
 * @param now Optional instant to evaluate intake windows against. Defaults to
 *            the current time (`new Date()`), injected here at the I/O boundary.
 *            Pass an explicit `Date` for deterministic tests.
 */
export function useActiveIntakeSeasons(now?: Date): UseActiveIntakeSeasonsResult {
  const { seasons: allSeasons, status: listStatus, refetch } = useSeasons();

  // Snapshot the explicit `now` (if any) into a primitive so the memo key is
  // stable; when `now` is undefined we evaluate against the current instant.
  const nowMs = now ? now.getTime() : undefined;

  // `activeIntakeSeasons` only filters/sorts the array it is given, returning the
  // very same Season objects. Its parameter/return use the infra `Season` type
  // (whose scheduling windows are optional), so we cast the result back to the
  // structurally-narrower FE `Season` — the elements are exactly the FE seasons
  // passed in, which always carry `intakeWindow` / `judgingWindow`.
  const seasons = useMemo<Season[]>(
    () =>
      activeIntakeSeasons(
        allSeasons,
        nowMs !== undefined ? new Date(nowMs) : new Date(),
      ) as Season[],
    [allSeasons, nowMs],
  );

  const status: SeasonResolutionStatus =
    listStatus === 'loading'
      ? 'loading'
      : seasons.length > 0
        ? 'resolved'
        : 'unresolved';

  return { seasons, status, refetch };
}
